import { Navigate, Outlet } from "react-router-dom";
import { BarChart3, ShieldCheck, Wallet } from "lucide-react";

import { useAuth } from "src/providers/AuthProvider";
import paylinqLogo from "src/images/paylinq-logo.png";
import paylinqLogoWhite from "src/images/paylinq-logo-white.png";

const highlights = [
	{ icon: Wallet, text: "Collect payments and settle straight to your bank" },
	{ icon: BarChart3, text: "Reconcile revenue, cost of sales and profit daily" },
	{ icon: ShieldCheck, text: "Track stock across every branch and warehouse" },
];

/**
 * Auth shell. The prototype starts signed in and has no auth design, so this is built
 * from the design system: a navy brand panel beside the form, collapsing to form-only
 * below `lg` (responsive convention, D3).
 */
const PublicRoute = () => {
	const { isAuthenticated } = useAuth();

	if (isAuthenticated) return <Navigate to="/dashboard" />;

	return (
		<div className="tw-grid tw-min-h-dvh lg:tw-grid-cols-[minmax(0,5fr),minmax(0,6fr)]">
			<aside className="tw-hidden tw-flex-col tw-justify-between tw-bg-gradient-to-br tw-from-navy tw-to-navy-deep tw-p-10 tw-text-white lg:tw-flex">
				<div className="tw-flex tw-items-center">
					<img src={paylinqLogoWhite} alt="Paylinq" className="tw-h-8 tw-w-auto" />
				</div>

				<div>
					<h2 className="tw-mb-3 tw-text-[30px] tw-font-bold tw-leading-tight">
						Simplify payments.
						<br />
						Streamline reconciliation.
					</h2>
					<p className="tw-mb-8 tw-max-w-md tw-text-[14px] tw-text-white/70">
						Manage settlements, collections and reconciliation effortlessly with one payment platform built for African SMEs.
					</p>

					<ul className="tw-space-y-3">
						{highlights.map(({ icon: Icon, text }) => (
							<li key={text} className="tw-flex tw-items-center tw-gap-3 tw-text-[13.5px] tw-text-white/85">
								<span className="tw-flex tw-h-8 tw-w-8 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-lg tw-bg-white/10 tw-text-teal">
									<Icon className="tw-h-4 tw-w-4" />
								</span>
								{text}
							</li>
						))}
					</ul>
				</div>

				<p className="tw-text-[12px] tw-text-white/45">© {new Date().getFullYear()} Paylinq. All rights reserved.</p>
			</aside>

			<main className="tw-flex tw-items-center tw-justify-center tw-bg-bg tw-px-5 tw-py-10 sm:tw-px-8">
				<div className="tw-w-full tw-max-w-[420px]">
					<div className="tw-mb-6 tw-flex tw-items-center lg:tw-hidden">
						<img src={paylinqLogo} alt="Paylinq" className="tw-h-8 tw-w-auto" />
					</div>

					<div className="tw-rounded-lg tw-border tw-border-border tw-bg-white tw-p-6 tw-shadow-card sm:tw-p-7">
						<Outlet />
					</div>
				</div>
			</main>
		</div>
	);
};

export default PublicRoute;
